"use client";

import { c as _c } from "react/compiler-runtime";
import { Button } from "@workspace/ui/components/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@workspace/ui/components/dropdown-menu";
import { cn } from "@workspace/ui/lib/utils";
import { Check, ChevronDown, Plus, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useReducer, useRef, useState, useSyncExternalStore } from "react";
import { type FloatingChatHistorySource, useFloatingChat } from "./floating-chat-context";
const NO_ITEMS: FloatingChatHistorySource["items"] = [];
function noUnsubscribe() {
  // No source to subscribe to.
}

/** Compact date label for history rows: Today / Yesterday / locale date. */
function relativeDay(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOfDay(new Date()) - startOfDay(date)) / 86_400_000);
  if (days <= 0) {
    return "Today";
  }
  if (days === 1) {
    return "Yesterday";
  }
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: date.getFullYear() === new Date().getFullYear() ? undefined : "numeric"
  });
}

/**
 * The header title as a dropdown: shows the active chat's title (falling back
 * to "New chat") and opens the conversation switcher — New chat first, then
 * the recent list with a check on the active entry. Rendered by the default
 * header whenever a `history` source is injected on `<FloatingChat.Root>`;
 * hosts composing their own header can drop it in directly.
 */
export function FloatingChatTitleMenu(t0) {
  const $ = _c(42);
  const {
    className
  } = t0;
  const {
    sources
  } = useFloatingChat();
  const history = sources.history;
  const [, bumpRevision] = useReducer(_temp, 0);
  let t1;
  if ($[0] !== history) {
    t1 = onChange => history?.subscribe?.(onChange) ?? noUnsubscribe;
    $[0] = history;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const subscribeItems = t1;
  let t2;
  if ($[2] !== history?.items) {
    t2 = () => history?.items ?? NO_ITEMS;
    $[2] = history?.items;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const readItems = t2;
  const items = useSyncExternalStore(subscribeItems, readItems, readItems);
  const [spinning, setSpinning] = useState(false);
  const spinGeneration = useRef(0);
  const handlerUnsub = useRef(null);
  let t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => () => handlerUnsub.current?.();
    t4 = [];
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  if (!history) {
    return null;
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = promise => {
      const gen = spinGeneration.current = spinGeneration.current + 1;
      setSpinning(true);
      const done = () => {
        if (spinGeneration.current === gen) {
          setSpinning(false);
        }
      };
      Promise.resolve(promise).then(done, done);
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const trackSpin = t5;
  let t6;
  if ($[7] !== bumpRevision || $[8] !== history) {
    t6 = nextOpen => {
      if (!nextOpen) {
        return;
      }
      handlerUnsub.current?.();
      handlerUnsub.current = history.subscribe?.(() => bumpRevision()) ?? null;
      if (history.refresh) {
        trackSpin(history.refresh());
      }
    };
    $[7] = bumpRevision;
    $[8] = history;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const handleOpenChange = t6;
  let t7;
  if ($[10] !== items || $[11] !== sources.activeChatId) {
    t7 = items.find(chat => chat.id === sources.activeChatId)?.title ?? "New chat";
    $[10] = items;
    $[11] = sources.activeChatId;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const activeTitle = t7;
  let t8;
  if ($[13] !== className) {
    t8 = cn("h-7 max-w-[14rem] min-w-0 gap-1 pr-1 pl-2 font-medium text-sm", className);
    $[13] = className;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t8) {
    t9 = <Button aria-label="Previous conversations" className={t8} size="sm" type="button" variant="ghost" />;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== activeTitle) {
    t10 = <span className="truncate">{activeTitle}</span>;
    $[17] = activeTitle;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <ChevronDown className="size-4 shrink-0 text-muted-foreground" />;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t10 || $[21] !== t9) {
    t12 = <DropdownMenuTrigger render={t9}>{t10}{t11}</DropdownMenuTrigger>;
    $[20] = t10;
    $[21] = t9;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  let t14;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Plus className="size-4" />;
    t14 = <span>New chat</span>;
    $[23] = t13;
    $[24] = t14;
  } else {
    t13 = $[23];
    t14 = $[24];
  }
  let t15;
  if ($[25] !== history || $[26] !== spinning) {
    t15 = history.refresh ? <button aria-label="Refresh chat list" className="ml-auto rounded-sm p-1 text-muted-foreground hover:bg-accent hover:text-foreground" onClick={event => {
      event.stopPropagation();
      trackSpin(history.refresh?.());
    }} type="button"><RefreshCw className={cn("size-3.5", spinning && "animate-spin")} /></button> : null;
    $[25] = history;
    $[26] = spinning;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== history.onNewChat || $[29] !== t15) {
    t16 = <DropdownMenuGroup><DropdownMenuItem className="pr-1" onClick={history.onNewChat}>{t13}{t14}{t15}</DropdownMenuItem></DropdownMenuGroup>;
    $[28] = history.onNewChat;
    $[29] = t15;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== history || $[32] !== items || $[33] !== sources.activeChatId) {
    t17 = items.length > 0 ? <><DropdownMenuSeparator /><DropdownMenuGroup><DropdownMenuLabel>Recent</DropdownMenuLabel><div className="max-h-72 overflow-y-auto">{items.map(chat_0 => <DropdownMenuItem key={chat_0.id} onClick={() => history.onSelect(chat_0.id)}><span className="truncate">{chat_0.title ?? "New chat"}</span><span className="ml-auto shrink-0 pl-2 text-muted-foreground text-xs">{relativeDay(chat_0.updatedAt)}</span>{chat_0.id === sources.activeChatId ? <Check className="size-4 shrink-0" /> : null}</DropdownMenuItem>)}</div></DropdownMenuGroup></> : null;
    $[31] = history;
    $[32] = items;
    $[33] = sources.activeChatId;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== t16 || $[36] !== t17) {
    t18 = <DropdownMenuContent align="start" className="w-64">{t16}{t17}</DropdownMenuContent>;
    $[35] = t16;
    $[36] = t17;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== handleOpenChange || $[39] !== t12 || $[40] !== t18) {
    t19 = <DropdownMenu onOpenChange={handleOpenChange}>{t12}{t18}</DropdownMenu>;
    $[38] = handleOpenChange;
    $[39] = t12;
    $[40] = t18;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  return t19;
}
function _temp(count) {
  return count + 1;
}